import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import {
  $getNodeByKey, $getSelection, $isNodeSelection,
  CLICK_COMMAND, COMMAND_PRIORITY_LOW, KEY_BACKSPACE_COMMAND, KEY_DELETE_COMMAND,
} from 'lexical';
import { FileText } from 'lucide-react';
import { usePageLink } from './PageLinkContext';

/**
 * The React face of a PageLinkNode: a full-width row chip (file icon + title).
 *
 * Three display states, decided by the host's PageLinkContext (see that file
 * for the value contract):
 *  - live:     `resolve(docId)` -> { exists: true, title } — live title, click
 *              calls `onOpen` with the doc id and the NODE's own scope.
 *  - deleted:  { exists: false } — muted line-through "Deleted page", click
 *              only selects (no onOpen). Still selectable/removable so the
 *              dead chip can be cleaned up.
 *  - fallback: no provider, or `resolve` returned null (tree still loading) —
 *              inert chip showing the node's creation-time cached title.
 *
 * Selection/removal follows the Lexical playground's image pattern:
 * useLexicalNodeSelection + KEY_BACKSPACE/KEY_DELETE registered here, acting
 * only while this node is part of a NodeSelection.
 */
export default function PageLinkComponent({ nodeKey, docId, scopeType, teamId, title }) {
  const [editor] = useLexicalComposerContext();
  const ctx = usePageLink();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const chipRef = useRef(null);

  const resolved = ctx && typeof ctx.resolve === 'function' ? ctx.resolve(docId) : null;
  const deleted = resolved != null && resolved.exists === false;
  const displayTitle = deleted
    ? 'Deleted page'
    : resolved != null && resolved.exists
      ? resolved.title || 'Untitled'
      : title || 'Untitled';

  // The node's OWN scope, not the host's — a chip pasted across scopes opens
  // in the scope it points at.
  const scope = useMemo(
    () =>
      scopeType === 'team' && teamId != null
        ? { type: 'team', teamId }
        : scopeType === 'device'
          ? { type: 'device' }
          : { type: 'personal' },
    [scopeType, teamId]
  );

  const onDelete = useCallback(
    (event) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        if (event) event.preventDefault();
        const node = $getNodeByKey(nodeKey);
        if (node) node.remove();
        return true;
      }
      return false;
    },
    [isSelected, nodeKey]
  );

  // Selection rides Lexical's OWN click pipeline (CLICK_COMMAND fires inside
  // its click update), not a React onClick: the editor's click handler runs
  // after any DOM handler and would immediately replace a selection set
  // there. Same reason the playground's image component does it this way.
  const onClickCommand = useCallback(
    (event) => {
      if (chipRef.current && (event.target === chipRef.current || chipRef.current.contains(event.target))) {
        if (!event.shiftKey) clearSelection();
        setSelected(true);
        return true;
      }
      return false;
    },
    [clearSelection, setSelected]
  );

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(CLICK_COMMAND, onClickCommand, COMMAND_PRIORITY_LOW),
        editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
        editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW)
      ),
    [editor, onClickCommand, onDelete]
  );

  // Navigation stays a plain DOM-side handler — onOpen flips the active tab
  // (a store write that can unmount this editor), which must never run inside
  // the editor.update the CLICK_COMMAND path executes in.
  const onClick = (event) => {
    event.preventDefault();
    if (!deleted && ctx && typeof ctx.onOpen === 'function') {
      ctx.onOpen({ docId, scope });
    }
  };

  return (
    <div
      ref={chipRef}
      className={`lx-page-link${isSelected ? ' lx-page-link--selected' : ''}${deleted ? ' lx-page-link--deleted' : ''}`}
      role="button"
      aria-disabled={deleted || undefined}
      onClick={onClick}
    >
      <FileText size={15} className="lx-page-link-icon" aria-hidden />
      <span className="lx-page-link-title">{displayTitle}</span>
    </div>
  );
}
